import { useState } from "react";

import { Box, Dialog, DialogContent, DialogTitle } from "@mui/material";

import { useAuthContext } from "hooks";
import { useWriterComponentState } from "hooks/useWriterComponentState";

import { CodeVerificationStep, PhoneEntryStep } from "./components";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  onComplete: VoidFunction;
}

type Step = "phone_entry" | "code_verification";

export function PhoneNumberSetup(props: Props) {
  const { open, onClose, onComplete } = props;
  const { accessToken } = useAuthContext();

  const [step, setStep] = useState<Step>("phone_entry");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const sendCodeWriter = useWriterComponentState({
    errorMessage: "Failed to send verification code.",
    writeConfig: {
      method: "post",
      url: "/user/phone-number",
      body: {
        phone_number: phoneNumber.replace(/\s/g, ""),
        access_token: accessToken,
      },
    },
    errorMessageKey: "message",
  });

  const verifyCodeWriter = useWriterComponentState({
    errorMessage: "Failed to verify code.",
    writeConfig: {
      method: "post",
      url: "/user/phone-number/verify",
      body: { access_token: accessToken, code: verificationCode },
    },
    errorMessageKey: "message",
  });

  const resendCodeWriter = useWriterComponentState({
    errorMessage: "Failed to resend code.",
    writeConfig: {
      method: "post",
      url: "/user/phone-number/resend-code",
      body: { access_token: accessToken },
    },
    errorMessageKey: "message",
  });

  const handleSendCode = () => {
    sendCodeWriter.write(() => {
      setStep("code_verification");
    });
  };

  const handleVerifyCode = () => {
    verifyCodeWriter.write(() => {
      onComplete();
    });
  };

  const handleResendCode = () => {
    resendCodeWriter.write();
  };

  const handleTryDifferentNumber = () => {
    setPhoneNumber("");
    setVerificationCode("");
    sendCodeWriter.resetState();
    verifyCodeWriter.resetState();
    setStep("phone_entry");
  };

  const handleClose = () => {
    setStep("phone_entry");
    setPhoneNumber("");
    setVerificationCode("");
    sendCodeWriter.resetState();
    verifyCodeWriter.resetState();
    resendCodeWriter.resetState();
    onClose();
  };

  const dialogTitle =
    step === "phone_entry" ? "Add Phone Number" : "Verify Your Phone";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box py={1}>
          {step === "phone_entry" && (
            <PhoneEntryStep
              phoneNumber={phoneNumber}
              onPhoneNumberChange={setPhoneNumber}
              onSendCode={handleSendCode}
              onCancel={handleClose}
              sendCodeWriter={sendCodeWriter}
            />
          )}
          {step === "code_verification" && (
            <CodeVerificationStep
              phoneNumber={phoneNumber}
              verificationCode={verificationCode}
              onCodeChange={setVerificationCode}
              onVerify={handleVerifyCode}
              onResend={handleResendCode}
              onTryDifferentNumber={handleTryDifferentNumber}
              verifyCodeWriter={verifyCodeWriter}
              resendCodeWriter={resendCodeWriter}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
